import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { Card } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { EmptyState } from "@/components/ui/EmptyState";
import { AddMemberModal } from "@/components/features/AddMemberModal";
import type { MemberProfile } from "@/lib/models";

const FAMILY_TYPE_ORDER = ["adult", "elderly_parent", "child"];

/**
 * Household member dashboard. Mirrors iOS
 * `Views/Members/MemberDashboardView.swift:11-186` — Family + Pets
 * sections, sorted by member type, with an "Add member" CTA.
 */
export function MembersPage() {
  const { members, loading } = useHouseholdContext();
  const t = useTranslations();
  const [showAddMember, setShowAddMember] = useState(false);

  const family = useMemo(() => {
    return members
      .filter((m) => m.memberType !== "pet")
      .filter((m) => m.isActive !== false)
      .sort((a, b) => {
        const ai = FAMILY_TYPE_ORDER.indexOf(a.memberType ?? "adult");
        const bi = FAMILY_TYPE_ORDER.indexOf(b.memberType ?? "adult");
        const aRank = ai === -1 ? FAMILY_TYPE_ORDER.length : ai;
        const bRank = bi === -1 ? FAMILY_TYPE_ORDER.length : bi;
        if (aRank !== bRank) return aRank - bRank;
        return (a.nickname ?? a.name ?? "").localeCompare(b.nickname ?? b.name ?? "");
      });
  }, [members]);

  const pets = useMemo(() => {
    return members
      .filter((m) => m.memberType === "pet")
      .filter((m) => m.isActive !== false);
  }, [members]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--accent-color, #00C7BE)", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("members.dashboard.title")}</h1>
        <button
          type="button"
          onClick={() => setShowAddMember(true)}
          className="px-3 py-1.5 rounded-lg bg-[color:var(--accent-color)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          {t("members.dashboard.addMember")}
        </button>
      </div>

      {/* Family */}
      <SectionHeader
        title={t("members.dashboard.family")}
        count={family.length}
        icon="👨‍👩‍👧‍👦"
      />
      {family.length === 0 ? (
        <Card>
          <EmptyState
            icon="👨‍👩‍👧"
            title={t("members.dashboard.familyEmpty.title")}
            subtitle={t("members.dashboard.familyEmpty.subtitle")}
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {family.map((m) => (
            <MemberRow key={m.uid} member={m} />
          ))}
        </div>
      )}

      {/* Pets */}
      {pets.length > 0 && (
        <>
          <SectionHeader
            title={t("members.dashboard.pets")}
            count={pets.length}
            icon="🐾"
          />
          <div className="space-y-2">
            {pets.map((m) => (
              <MemberRow key={m.uid} member={m} />
            ))}
          </div>
        </>
      )}

      {showAddMember && (
        <AddMemberModal onClose={() => setShowAddMember(false)} />
      )}
    </div>
  );
}

function MemberRow({ member }: { member: MemberProfile }) {
  const t = useTranslations();
  const displayName = member.nickname || member.name || t("members.detail.unknownMember");
  const subtitle = formatSubtitle(member, t);
  return (
    <Link to={`/account/members/${member.uid}`} className="block">
      <Card>
        <div className="flex items-center gap-3">
          <Avatar src={member.profile_url} alt={displayName} size={44} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold truncate">{displayName}</p>
              {member.memberType && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300">
                  {t(memberTypeLabelKey(member.memberType))}
                </span>
              )}
              {member.authUid && (
                <span
                  className="text-emerald-500"
                  title={t("members.dashboard.authLinked")}
                  aria-label={t("members.dashboard.authLinked")}
                >
                  ✓
                </span>
              )}
            </div>
            {subtitle && (
              <p className="text-xs text-gray-400 truncate mt-0.5">{subtitle}</p>
            )}
          </div>
          <svg
            className="text-gray-300 shrink-0"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </div>
      </Card>
    </Link>
  );
}

function memberTypeLabelKey(memberType: string): string {
  switch (memberType) {
    case "adult":
      return "register.memberType.adult";
    case "child":
      return "register.memberType.child";
    case "elderly_parent":
      return "register.memberType.elderly_parent";
    case "pet":
      return "members.dashboard.petLabel";
    default:
      return "members.dashboard.guestLabel";
  }
}

function formatSubtitle(
  member: MemberProfile,
  _t: (key: string, params?: Record<string, string | number>) => string,
): string | null {
  if (member.birthMonth && member.birthDay) {
    const monthName = new Date(2000, member.birthMonth - 1, 1).toLocaleDateString(undefined, {
      month: "short",
    });
    return `${monthName} ${member.birthDay}`;
  }
  if (member.memberType === "pet" && member.details?.breed) {
    return member.details.breed;
  }
  if (member.notes) {
    const trimmed = member.notes.trim();
    return trimmed.length > 60 ? `${trimmed.slice(0, 60)}…` : trimmed;
  }
  return null;
}
